(() => {
  'use strict';
  if (window.KaliaConfigGuides) return;

  const states = new WeakMap();
  const bound = new WeakSet();
  let active = null;
  let locked = null;

  function lockScroll() {
    if (locked) return;
    locked = {
      htmlOverflow: document.documentElement.style.overflow,
      htmlPriority: document.documentElement.style.getPropertyPriority('overflow'),
      bodyOverflow: document.body.style.overflow,
      bodyPriority: document.body.style.getPropertyPriority('overflow')
    };
    document.documentElement.style.setProperty('overflow', 'hidden', 'important');
    document.body.style.setProperty('overflow', 'hidden', 'important');
  }

  function unlockScroll() {
    if (!locked) return;
    document.documentElement.style.setProperty('overflow', locked.htmlOverflow, locked.htmlPriority);
    document.body.style.setProperty('overflow', locked.bodyOverflow, locked.bodyPriority);
    locked = null;
  }

  function finishClose(dialog) {
    const state = states.get(dialog);
    if (!state) return;
    states.delete(dialog);
    if (active === dialog) {
      active = null;
      unlockScroll();
    }
    if (state.restoreFocus && state.opener?.isConnected && !state.opener.disabled) {
      state.opener.focus({ preventScroll: true });
    }
  }

  function close(dialog = active, restoreFocus = true) {
    if (!dialog || !states.has(dialog)) return;
    states.get(dialog).restoreFocus = restoreFocus;
    if (dialog.open) dialog.close();
    finishClose(dialog);
  }

  function bind(dialog) {
    if (bound.has(dialog)) return;
    bound.add(dialog);
    dialog.addEventListener('cancel', event => {
      event.preventDefault();
      close(dialog);
    });
    dialog.addEventListener('close', () => {
      // A queued close event from a previous opening must not close a newly reopened guide.
      if (!dialog.open) finishClose(dialog);
    });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) close(dialog);
    });
  }


  // Read the existing, valid form selection. This summary never changes an order choice.
  function renderInstallationSummary(dialog) {
    const panels = dialog.querySelectorAll('[data-kalia-install-summary]');
    if (!panels.length) return;
    const studio = dialog.closest('kalia-product-studio');
    const form = studio?.querySelector('[data-studio-form]');
    const rows = [];
    const seen = new Set();
    try {
      if (studio?.ready && form && !form.hidden) {
        for (const fieldset of form.querySelectorAll('fieldset[data-field-id]')) {
          const id = fieldset.dataset.fieldId;
          const fields = studio.config?.fields?.filter(field => field.id === id) || [];
          if (fields.length !== 1) continue;
          const field = fields[0];
          const title = String(field.title || '').replace(/[\u200B-\u200D\uFEFF]/g, '').trim();
          const name = /^mount type$/i.test(title) ? 'Mount' : /^control type$/i.test(title) ? 'Control' : /^valance$/i.test(title) ? 'Valance' : '';
          if (!name || seen.has(name) || !studio.enabledIds?.has(id) || !studio.fieldVisible?.(id) ||
            fieldset.hidden || fieldset.closest('[hidden]') || studio.widgets?.get(id)?.fieldset !== fieldset) continue;
          const checked = [...fieldset.querySelectorAll('input:checked')].filter(input =>
            input.name === `choice_${id}` && ['radio', 'checkbox'].includes(input.type));
          if (checked.length !== 1) continue;
          const input = checked[0];
          const option = field.options?.filter(item => item.id === input.value);
          if (input.disabled || input.matches(':disabled') || input.hidden || input.closest('[hidden]') ||
            option?.length !== 1 || !Number.isInteger(option[0].feeCents) || option[0].feeCents < 0 ||
            studio.choices?.[id] !== input.value ||
            !studio.widgets.get(id).inputs?.some(item => item.input === input && item.option?.id === input.value)) continue;
          const label = input.closest('label')?.querySelector('b')?.textContent?.trim();
          if (!label || label.length > 200) continue;
          seen.add(name); rows.push({ name, label });
        }
      }
    } catch (_) { rows.length = 0; seen.clear(); }
    const order = ['Mount', 'Control', 'Valance'];
    rows.sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name));
    for (const panel of panels) {
      const list = panel.querySelector('[data-kalia-install-choices]');
      const pending = panel.querySelector('[data-kalia-install-pending]');
      if (!list || !pending) continue;
      list.replaceChildren();
      for (const row of rows) {
        const item = document.createElement('li');
        const label = document.createElement('strong');
        label.textContent = `${row.name}: `;
        item.append(label, document.createTextNode(row.label)); list.append(item);
      }
      list.hidden = !rows.length;
      pending.hidden = rows.length > 0;
    }
  }

  function open(dialog, opener) {
    if (!dialog?.matches('dialog[data-kalia-config-guide]') || typeof dialog.showModal !== 'function') return false;
    if (active === dialog && dialog.open) return true;
    let returnTo = opener || document.activeElement;
    if (active && active.contains(returnTo)) returnTo = states.get(active)?.opener || returnTo;
    if (active) close(active, false);
    if (dialog.open) dialog.close();
    bind(dialog);
    renderInstallationSummary(dialog);
    states.set(dialog, { opener: returnTo, restoreFocus: true });
    active = dialog;
    lockScroll();
    try {
      dialog.showModal();
      dialog.querySelector('[data-kalia-guide-close]')?.focus({ preventScroll: true });
      return true;
    } catch (_) {
      close(dialog, false);
      return false;
    }
  }

  document.addEventListener('click', event => {
    const trigger = event.target.closest?.('[data-kalia-guide-open]');
    if (trigger) {
      const id = trigger.getAttribute('data-kalia-guide-open');
      const dialog = id ? document.getElementById(id) : null;
      if (open(dialog, trigger)) event.preventDefault();
      return;
    }
    const button = event.target.closest?.('[data-kalia-guide-close]');
    const dialog = button?.closest('dialog[data-kalia-config-guide]');
    if (dialog && states.has(dialog)) {
      event.preventDefault();
      close(dialog);
    }
  });
  for (const name of ['kalia:product-configuration-change', 'kalia:product-studio-ready']) {
    document.addEventListener(name, event => {
      if (active?.open && active.closest('kalia-product-studio') === event.target) renderInstallationSummary(active);
    });
  }
  document.addEventListener('shopify:section:unload', event => {
    if (active && event.target.contains?.(active)) close(active, false);
  });
  window.KaliaConfigGuides = Object.freeze({ open, close });
})();
